/**
 * CommandPalette - Terminal-style navigation for Matrix Terminal theme
 *
 * Triggered by '/' or ':' key. Provides fuzzy-match command navigation.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

interface Command {
  name: string
  aliases: string[]
  path: string
  description: string
}

const COMMANDS: Command[] = [
  { name: 'projects', aliases: ['proj', 'work', 'portfolio'], path: '/projects', description: 'View projects' },
  { name: 'experience', aliases: ['exp', 'career', 'jobs'], path: '/background', description: 'Career history' },
  { name: 'writings', aliases: ['write', 'blog', 'articles'], path: '/writings', description: 'Articles & posts' },
  { name: 'interests', aliases: ['hobbies', 'about'], path: '/interests', description: 'Personal interests' },
  { name: 'organizations', aliases: ['orgs', 'companies'], path: '/organizations', description: 'Organizations' },
  { name: 'roles', aliases: ['positions', 'jobs'], path: '/roles', description: 'Available roles' },
  { name: 'home', aliases: ['index', 'main', 'portal'], path: '/', description: 'Return to portal' },
  { name: 'config', aliases: ['settings', 'preferences', 'prefs'], path: '__settings__', description: 'Open settings' },
  { name: 'help', aliases: ['?', 'commands', 'h'], path: '__help__', description: 'Show all commands' },
]

interface CommandPaletteProps {
  onOpenSettings?: () => void
}

export function CommandPalette({ onOpenSettings }: CommandPaletteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showHelp, setShowHelp] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Fuzzy match commands
  const getMatches = useCallback((query: string): Command[] => {
    if (!query) return COMMANDS.slice(0, 5)

    const lowerQuery = query.toLowerCase().replace(/^(cd |goto |open )/, '')

    return COMMANDS.filter(cmd => {
      const nameMatch = cmd.name.toLowerCase().includes(lowerQuery)
      const aliasMatch = cmd.aliases.some(a => a.toLowerCase().includes(lowerQuery))
      const descMatch = cmd.description.toLowerCase().includes(lowerQuery)
      return nameMatch || aliasMatch || descMatch
    }).slice(0, 6)
  }, [])

  const matches = getMatches(input)

  // Handle keyboard trigger
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      // Open on '/' or ':'
      if ((e.key === '/' || e.key === ':') && !isOpen) {
        e.preventDefault()
        setIsOpen(true)
        setInput('')
        setSelectedIndex(0)
        setShowHelp(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Handle input keyboard navigation
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
      case 'q':
        if (e.key === 'q' && input) break // Allow typing 'q'
        e.preventDefault()
        setIsOpen(false)
        break
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, matches.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        executeCommand(matches[selectedIndex])
        break
      case 'Tab':
        e.preventDefault()
        if (matches[selectedIndex]) {
          setInput(matches[selectedIndex].name)
        }
        break
    }
  }

  const executeCommand = (cmd: Command | undefined) => {
    if (!cmd) return

    if (cmd.path === '__settings__') {
      setIsOpen(false)
      onOpenSettings?.()
    } else if (cmd.path === '__help__') {
      setShowHelp(true)
    } else {
      setIsOpen(false)
      navigate(cmd.path)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="command-palette-overlay"
      onClick={() => setIsOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '20vh',
        zIndex: 9999,
      }}
    >
      <div
        className="command-palette"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '500px',
          backgroundColor: '#0a0a0a',
          border: '1px solid #0f0',
          borderRadius: '4px',
          fontFamily: "'Courier New', monospace",
          boxShadow: '0 0 20px rgba(0, 255, 0, 0.2)',
        }}
      >
        {/* Input area */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #0f03',
          }}
        >
          <span style={{ color: '#0f0', marginRight: '8px' }}>&gt;</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => {
              setInput(e.target.value)
              setSelectedIndex(0)
              setShowHelp(false)
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="goto projects..."
            style={{
              flex: 1,
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#0f0',
              fontSize: '16px',
              fontFamily: 'inherit',
            }}
            autoComplete="off"
            spellCheck={false}
          />
          <span
            style={{
              color: '#0f06',
              fontSize: '12px',
            }}
          >
            ESC to close
          </span>
        </div>

        {/* Results */}
        {showHelp ? (
          <div style={{ padding: '12px 16px' }}>
            <div style={{ color: '#0f0', marginBottom: '12px' }}>
              Available commands:
            </div>
            {COMMANDS.map(cmd => (
              <div
                key={cmd.name}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  color: '#0f0',
                  opacity: 0.8,
                }}
              >
                <span>{cmd.name}</span>
                <span style={{ opacity: 0.5 }}>{cmd.description}</span>
              </div>
            ))}
            <div style={{ marginTop: '12px', color: '#0f06', fontSize: '12px' }}>
              Prefixes: cd, goto, open (optional)
            </div>
          </div>
        ) : (
          <div style={{ padding: '8px 0' }}>
            {matches.map((cmd, i) => (
              <div
                key={cmd.name}
                onClick={() => executeCommand(cmd)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  backgroundColor: i === selectedIndex ? '#0f01' : 'transparent',
                  borderLeft: i === selectedIndex ? '2px solid #0f0' : '2px solid transparent',
                }}
              >
                <span style={{ color: '#0f0', marginRight: '12px' }}>
                  {i === selectedIndex ? '→' : ' '}
                </span>
                <span style={{ color: '#0f0', flex: 1 }}>{cmd.name}</span>
                <span style={{ color: '#0f06', fontSize: '12px' }}>
                  {cmd.path === '__settings__' || cmd.path === '__help__'
                    ? cmd.description
                    : cmd.path}
                </span>
              </div>
            ))}
            {matches.length === 0 && (
              <div style={{ padding: '8px 16px', color: '#f006' }}>
                No matching commands. Type 'help' for available commands.
              </div>
            )}
          </div>
        )}

        {/* Footer hint */}
        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid #0f03',
            color: '#0f04',
            fontSize: '11px',
            display: 'flex',
            gap: '16px',
          }}
        >
          <span>↑↓ navigate</span>
          <span>⏎ select</span>
          <span>⇥ autocomplete</span>
          <span>esc quit</span>
        </div>
      </div>
    </div>
  )
}

export default CommandPalette
